import React, { useEffect, useRef, useState } from 'react'

const AudioPlayer = ({ src, peaks, duration, variant = 'primary', id, playingId, stopTrigger, onPlayStateChange }) => {
  const containerRef = useRef(null)
  const wsRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [ready, setReady] = useState(false)

  const waveColor = variant === 'secondary' ? '#8ab4ff' : '#aaaaaa'
  const progressColor = variant === 'secondary' ? '#ffffff' : '#305CE1'

  // Pause when carousel navigates
  useEffect(() => {
    if (stopTrigger > 0) wsRef.current?.pause()
  }, [stopTrigger])

  // Pause when another player starts
  useEffect(() => {
    if (playingId !== null && playingId !== id) wsRef.current?.pause()
  }, [playingId, id])

  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) return

    // Reset visual state immediately on src change so old card's state doesn't bleed through
    setIsPlaying(false)
    setReady(false)

    let cancelled = false
    import('wavesurfer.js').then(({ default: WaveSurfer }) => {
      if (cancelled) return
      const ws = WaveSurfer.create({
        container: containerRef.current,
        waveColor,
        progressColor,
        height: 30,
        barWidth: 2,
        cursorColor: 'transparent',
        responsive: true,
        // Precomputed peaks + duration draw the waveform instantly and stream
        // audio lazily via a media element — avoids the multi-second fetch+decode.
        url: src,
        ...(peaks ? { peaks: [peaks], duration } : {}),
      })
      // With peaks supplied the waveform is already drawn; enable controls now
      // rather than waiting for the full media 'ready' event.
      if (peaks) setReady(true)
      ws.on('ready', () => setReady(true))
      ws.on('play', () => { setIsPlaying(true); onPlayStateChange?.(id, true) })
      ws.on('pause', () => { setIsPlaying(false); onPlayStateChange?.(id, false) })
      ws.on('finish', () => { setIsPlaying(false); onPlayStateChange?.(id, false) })
      // Suppress fetch-abort errors thrown when destroy() cancels an in-flight load
      ws.on('error', (err) => {
        if (err?.name === 'AbortError' || String(err?.message).toLowerCase().includes('abort')) return
        console.error('WaveSurfer error:', err)
      })
      wsRef.current = ws
    })

    return () => {
      cancelled = true
      try { wsRef.current?.destroy() } catch (_) { /* suppress abort on unmount */ }
      wsRef.current = null
      onPlayStateChange?.(id, false)
    }
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => wsRef.current?.playPause()

  return (
    <div className={`aac-audio-player aac-audio-player--${variant}`}>
      <button
        className="aac-audio-player__btn"
        onClick={toggle}
        disabled={!ready}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>
      <div ref={containerRef} className="aac-audio-player__wave" />
    </div>
  )
}

export default AudioPlayer
